import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import videoServiceInstance from '../../../services/videoService';
import songFileServiceInstance from '../../../services/songFileService';
import SongFileUploadModal from './SongFileUploadModal';
import EnhancedVideoPlayer from '../../../components/ui/EnhancedVideoPlayer';
import FollowButton from '../../../components/ui/FollowButton';
import { getTalentById } from '../../../services/talentService';

import { useMidiPlayer } from '../../../hooks/useMidiPlayer';
import { useMidiLyrics } from '../../../hooks/useMidiLyrics';
import KaraokeModal from './KaraokeModal';
import MuxVideoSection from './MuxVideoSection';


export default function SongDetailView({ song, characterAnalyses, selectedCharacter, onCharacterChange, onSongUpdate }) {
  const [characterAnalysis, setCharacterAnalysis] = useState(null);
  const [selectedCharacterIndex, setSelectedCharacterIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [videoUrls, setVideoUrls] = useState({});
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [songFiles, setSongFiles] = useState({ midiUrl: null, mp3Url: null });
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [showKaraokeModal, setShowKaraokeModal] = useState(false);
  const [talentInfo, setTalentInfo] = useState(null);

  // MIDI player hook for song-level files only
  const songMidiPlayer = useMidiPlayer(songFiles?.midiUrl);

  // MIDI lyrics detection hook
  const songLyricsPlayer = useMidiLyrics(songFiles?.midiUrl);

  useEffect(() => {
    if (characterAnalyses?.length > 0) {
      const initialCharacter = selectedCharacter || characterAnalyses?.[0];
      setCharacterAnalysis(initialCharacter);
      setSelectedCharacterIndex(0);
      loadVideosForCharacter(initialCharacter?.id);
    } else {
      setCharacterAnalysis(null);
    }
  }, [characterAnalyses, selectedCharacter]);

  useEffect(() => {
    loadSongFiles();
    loadTalentInfo();
  }, [song]);

  const loadTalentInfo = async () => {
    if (!song?.talent_id) {
      setTalentInfo(null);
      return;
    }

    try {
      const { data, error } = await getTalentById(song?.talent_id);
      if (!error && data) {
        setTalentInfo(data);
      }
    } catch (err) {
      console.error('Error loading talent info:', err);
    }
  };

  const loadSongFiles = async () => {
    if (!song) return;

    try {
      setLoadingFiles(true);
      const midiUrl = song?.midi_file_path 
        ? await songFileServiceInstance?.getSongFileUrl(song?.midi_file_path)
        : null;
      const mp3Url = song?.mp3_file_path
        ? await songFileServiceInstance?.getSongFileUrl(song?.mp3_file_path)
        : null;

      setSongFiles({ midiUrl, mp3Url });
    } catch (err) {
      console.error('Error loading song files:', err);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleRemoveFile = async (fileType) => {
    if (!window.confirm(`Are you sure you want to remove this ${fileType?.toUpperCase()} file?`)) {
      return;
    }

    try {
      setLoading(true);

      // Get current file path
      const filePath = fileType === 'midi'
        ? song?.midi_file_path
        : song?.mp3_file_path;

      if (filePath) {
        // Delete from storage
        await songFileServiceInstance?.deleteSongFile(filePath);
        // Remove from database
        await songFileServiceInstance?.removeSongFilePath(song?.id, fileType);
      }

      // Reload song files
      await loadSongFiles();

      alert(`${fileType?.toUpperCase()} file removed successfully!`);
    } catch (error) {
      console.error('Error removing file:', error);
      alert(`Failed to remove ${fileType?.toUpperCase()} file: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadComplete = async () => {
    // Reload files after successful upload
    await loadSongFiles();
    // Notify parent component to refresh song data
    if (onSongUpdate) {
      onSongUpdate();
    }
  };

  const handlePlayAudio = (fileType) => {
    // Stop any currently playing audio first
    handleStopAudio();
    
    if (fileType === 'midi') {
      songMidiPlayer?.play();
    }
    
    setPlayingAudio(fileType);
  };

  const handleStopAudio = () => {
    // Stop MIDI player
    songMidiPlayer?.stop();
    
    setPlayingAudio(null);
  };

  const handleOpenKaraoke = (midiUrl) => {
    setShowKaraokeModal(true);
  };

  const handleCloseKaraoke = () => {
    setShowKaraokeModal(false);
  };

  const loadVideosForCharacter = async (characterAnalysisId) => {
    if (!characterAnalysisId) {
      setLoadingVideos(false);
      return;
    }

    try {
      setLoadingVideos(true);
      const { data: videos, error: videoError } = await videoServiceInstance?.getCharacterAnalysisVideos(characterAnalysisId);
      
      if (videoError) {
        console.error('Error loading videos:', videoError);
        setLoadingVideos(false);
        return;
      }

      // Generate video URLs for all videos
      const urls = {};
      for (const video of videos || []) {
        if (video?.video_path) {
          const url = await videoServiceInstance?.getVideoUrl(video?.video_path);
          const thumbnailUrl = video?.thumbnail_path 
            ? await videoServiceInstance?.getThumbnailUrl(video?.thumbnail_path)
            : null;
          
          const key = `${video?.level}_${video?.subsection || 'main'}`;
          urls[key] = {
            videoUrl: url,
            thumbnailUrl: thumbnailUrl,
            duration: video?.duration_seconds,
            videoData: video
          };
        }
      }
      
      setVideoUrls(urls);
    } catch (err) {
      console.error('Error loading videos:', err);
    } finally {
      setLoadingVideos(false);
    }
  };

  const handleCharacterSelect = (index) => {
    const selectedChar = characterAnalyses?.[index];
    setSelectedCharacterIndex(index);
    setCharacterAnalysis(selectedChar);
    onCharacterChange?.(selectedChar);
    loadVideosForCharacter(selectedChar?.id);
    // Reset to overview tab when switching characters
    setActiveTab('overview');
  };

  const handleRemoveVideo = async (level, subsection = 'main') => {
    const key = `${level}_${subsection}`;
    const videoData = videoUrls?.[key]?.videoData;
    
    if (!videoData?.id) {
      alert('Video ID not found');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this video?')) {
      return;
    }

    try {
      setLoading(true);
      const { error } = await videoServiceInstance?.deleteVideo(videoData?.id);
      
      if (error) {
        throw new Error(error?.message || 'Failed to delete video');
      }

      // Remove from local state
      setVideoUrls(prev => {
        const updated = { ...prev };
        delete updated?.[key];
        return updated;
      });

      alert('Video removed successfully!');
    } catch (error) {
      console.error('Error removing video:', error);
      alert('Failed to remove video: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const renderFileSection = (fileType, fileUrl) => {
    const label = fileType === 'midi' ? 'MIDI File' : 'MP3 File';
    const isMP3 = fileType === 'mp3';
    const isMIDI = fileType === 'midi';
    const isPlaying = playingAudio === fileType;
    
    return (
      <div className="mb-4">
        <h4 className="text-md font-semibold text-white mb-2">{label}</h4>
        {fileUrl ? (
          <div className="space-y-2">
            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1">
                  <Icon name={fileType === 'midi' ? 'Music' : 'Volume2'} size={24} className="text-blue-300" />
                  <div className="flex-1">
                    <p className="text-white font-medium">{label} Available</p>
                    <div className="flex items-center space-x-3 mt-1">
                      <a 
                        href={fileUrl} 
                        download 
                        className="text-sm text-blue-300 hover:text-blue-200 transition-colors flex items-center"
                      >
                        <Icon name="Download" size={14} className="mr-1" />
                        Download
                      </a>
                      {(isMP3 || isMIDI) && (
                        <button
                          onClick={() => isPlaying ? handleStopAudio() : handlePlayAudio(fileType)}
                          disabled={isMIDI && songMidiPlayer?.isLoading}
                          className="text-sm text-green-300 hover:text-green-200 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name={isPlaying ? 'StopCircle' : 'PlayCircle'} size={14} className="mr-1" />
                          {songMidiPlayer?.isLoading ? 'Loading...' : (isPlaying ? 'Stop' : 'Play')}
                        </button>
                      )}
                      {isMIDI && songLyricsPlayer?.hasLyrics && (
                        <button
                          onClick={() => handleOpenKaraoke(fileUrl)}
                          className="text-sm text-yellow-300 hover:text-yellow-200 transition-colors flex items-center"
                        >
                          <Icon name="Mic2" size={14} className="mr-1" />
                          View Karaoke
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveFile(fileType)}
                  disabled={loading}
                  className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm ml-3"
                >
                  Remove
                </button>
              </div>
              
              {/* MIDI Player Controls */}
              {isMIDI && isPlaying && !songMidiPlayer?.error && (
                <div className="mt-4 pt-4 border-t border-white border-opacity-20">
                  <div className="space-y-3">
                    {/* Progress Bar */}
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-blue-200 min-w-[40px]">
                        {Math.floor(songMidiPlayer?.progress || 0)}%
                      </span>
                      <div className="flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-blue-500 h-full transition-all duration-300"
                          style={{ width: `${songMidiPlayer?.progress || 0}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Playback Controls */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => songMidiPlayer?.isPlaying ? songMidiPlayer?.pause() : songMidiPlayer?.play()}
                        className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center"
                      >
                        <Icon name={songMidiPlayer?.isPlaying ? 'Pause' : 'Play'} size={14} className="mr-1" />
                        {songMidiPlayer?.isPlaying ? 'Pause' : 'Play'}
                      </button>
                      <button
                        onClick={() => songMidiPlayer?.stop()}
                        className="px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm flex items-center"
                      >
                        <Icon name="Square" size={14} className="mr-1" />
                        Stop
                      </button>
                    </div>
                    
                    {/* Info Text */}
                    <p className="text-xs text-blue-200">
                      🎹 High-quality SoundFont playback enabled
                    </p>
                  </div>
                </div>
              )}

              {/* MIDI Error Display */}
              {isMIDI && songMidiPlayer?.error && (
                <div className="mt-4 pt-4 border-t border-white border-opacity-20">
                  <p className="text-sm text-red-300">
                    ⚠️ {songMidiPlayer?.error}. Please try downloading the file instead.
                  </p>
                </div>
              )}
              
              {/* Audio Player for MP3 files */}
              {isMP3 && isPlaying && (
                <div className="mt-4 pt-4 border-t border-white border-opacity-20">
                  <audio
                    src={fileUrl}
                    controls
                    autoPlay
                    className="w-full"
                    onEnded={handleStopAudio}
                    onError={() => {
                      alert('Failed to load audio file. Please try downloading instead.');
                      handleStopAudio();
                    }}
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
            </div>
          </div>
        ) : (
          <p className="text-blue-200 text-sm">No {label?.toLowerCase()} uploaded yet</p>
        )}
      </div>
    );
  };

  const renderVideoSection = (level, subsection = 'main', title) => {
    const key = `${level}_${subsection}`;
    const videoInfo = videoUrls?.[key];

    if (!videoInfo?.videoUrl) {
      return null;
    }

    return (
      <div className="mb-6">
        <h4 className="text-md font-semibold text-white mb-3">{title}</h4>
        <div className="space-y-2">
          <div className="relative aspect-video bg-gray-900 bg-opacity-50 rounded-lg overflow-hidden max-w-2xl">
            <EnhancedVideoPlayer
              src={videoInfo?.videoUrl}
              poster={videoInfo?.thumbnailUrl}
              className="w-full h-full"
            />
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => handleRemoveVideo(level, subsection)}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Removing...' : 'Remove Video'}
            </button>
            {videoInfo?.duration && (
              <span className="text-sm text-blue-200">
                Duration: {Math.floor(videoInfo?.duration / 60)}:{(videoInfo?.duration % 60)?.toString()?.padStart(2, '0')}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Overview Tab Content
  const renderOverviewTab = () => {
    const overview = characterAnalysis?.overview || {};

    return (
      <div className="space-y-6">
        {/* Talent Info Section */}
        {talentInfo && (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Icon name="User" size={20} className="mr-2" />
              Taught By
            </h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {talentInfo?.headshot_url ? (
                  <img
                    src={talentInfo?.headshot_url}
                    alt={`${talentInfo?.first_name} ${talentInfo?.last_name}`}
                    className="w-16 h-16 rounded-full object-cover border-2 border-white border-opacity-20"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <Icon name="User" size={32} className="text-white opacity-75" />
                  </div>
                )}
                <div>
                  <Link
                    to={`/talent-detail-page/${talentInfo?.id}`}
                    className="text-white hover:text-blue-200 hover:underline transition-colors text-lg font-semibold"
                  >
                    {talentInfo?.display_name || `${talentInfo?.first_name} ${talentInfo?.last_name}`}
                  </Link>
                  {talentInfo?.bio && (
                    <p className="text-blue-200 text-sm mt-1 line-clamp-2">{talentInfo?.bio}</p>
                  )}
                </div>
              </div>
              <FollowButton talentId={talentInfo?.id} onFollowChange={() => {}} />
            </div>
          </div>
        )}

        {/* Mux Video Section for Overview */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <MuxVideoSection songId={song?.id} level="overview" />
        </div>

        {/* Upload Button */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <Icon name="Upload" size={20} className="mr-2" />
              Song Files
            </h3>
            <button
              onClick={() => setShowUploadModal(true)}
              disabled={loading || loadingFiles}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <Icon name="Upload" size={18} className="mr-2" />
              Upload Files
            </button>
          </div>

          {/* Song-level Files (single source) */}
          <div className="p-4 bg-purple-500 bg-opacity-20 border border-purple-400 border-opacity-30 rounded-lg">
            <h4 className="text-md font-semibold text-white mb-3 flex items-center">
              <Icon name="Music" size={18} className="mr-2" />
              Song Files (Shared Across All Characters)
            </h4>
            <p className="text-sm text-blue-200 mb-4">
              These MIDI and MP3 files are shared across all character analyses for this song. Upload once, use everywhere.
            </p>
            {renderFileSection('midi', songFiles?.midiUrl)}
            {renderFileSection('mp3', songFiles?.mp3Url)}
          </div>
        </div>

        {/* Character Analysis - Matching Edit Form Fields */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <Icon name="FileText" size={24} className="mr-2" />
            Overview
            <span className="text-xs bg-blue-500 bg-opacity-20 text-blue-200 px-2 py-1 rounded-full ml-2">
              Character-Specific Analysis
            </span>
          </h3>
          <div className="space-y-4 text-blue-50">
            {/* Character Name */}
            {(overview?.character || characterAnalysis?.role?.character_name) && (
              <div>
                <p className="font-semibold text-blue-100 mb-1">Character Name:</p>
                {characterAnalysis?.role?.id ? (
                  <Link
                    to={`/role-detail-page/${characterAnalysis?.role?.id}`}
                    className="text-white hover:text-blue-200 hover:underline transition-colors cursor-pointer text-lg"
                  >
                    {overview?.character || characterAnalysis?.role?.character_name}
                  </Link>
                ) : (
                  <p className="text-white">{overview?.character || characterAnalysis?.role?.character_name}</p>
                )}
              </div>
            )}

            {/* Emotional Core */}
            {overview?.emotionalCore && (
              <div>
                <p className="font-semibold text-blue-100 mb-1">Emotional Core:</p>
                <p className="text-white">{overview?.emotionalCore}</p>
              </div>
            )}

            {/* Objective */}
            {overview?.objective && (
              <div>
                <p className="font-semibold text-blue-100 mb-1">Objective:</p>
                <p className="text-white">{overview?.objective}</p>
              </div>
            )}

            {/* Vocal Range */}
            {(overview?.vocalRange || characterAnalysis?.role?.vocal_range) && (
              <div>
                <p className="font-semibold text-blue-100 mb-1">Vocal Range:</p>
                <p className="text-white">{overview?.vocalRange || characterAnalysis?.role?.vocal_range}</p>
              </div>
            )}

            {/* Character Analysis Notes */}
            {overview?.notes && (
              <div>
                <p className="font-semibold text-blue-100 mb-1">Character Analysis Notes:</p>
                <p className="text-white leading-relaxed whitespace-pre-wrap">{overview?.notes}</p>
              </div>
            )}

            {/* Show message if no data */}
            {!overview?.character && !characterAnalysis?.role?.character_name && 
             !overview?.emotionalCore && !overview?.objective && 
             !overview?.vocalRange && !characterAnalysis?.role?.vocal_range && 
             !overview?.notes && (
              <p className="text-blue-200 text-center italic">No overview information available</p>
            )}
          </div>

          {/* Information Notice - Matching Edit Form */}
          <div className="bg-blue-500 bg-opacity-10 border border-blue-400 border-opacity-30 rounded-lg p-4 mt-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-blue-300 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-blue-100 mb-1">
                  Character-Specific Overview
                </h4>
                <p className="text-xs text-blue-200 leading-relaxed">
                  This Overview section focuses on character-specific elements: Character Name (auto-filled), Emotional Core, Objective, and Vocal Range. 
                  Musical and Song Title information is managed in the Song Information section above, as it applies to all character versions of this song.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Level 1 Tab Content
  const renderLevel1Tab = () => {
    const level1 = characterAnalysis?.level1 || '';
    
    return (
      <div className="space-y-6">
        {/* Mux Video Section for Level 1 */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <MuxVideoSection songId={song?.id} level="level_1" />
        </div>

        {/* Level 1 Analysis */}
        {level1 && (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <Icon name="Award" size={24} className="mr-2" />
              Level 1 Analysis
            </h3>
            <div className="text-blue-50 leading-relaxed whitespace-pre-wrap">{level1}</div>
          </div>
        )}
      </div>
    );
  };

  // Level 2 Tab Content
  const renderLevel2Tab = () => {
    const level2 = characterAnalysis?.level2 || '';
    
    return (
      <div className="space-y-6">
        {/* Mux Video Section for Level 2 */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <MuxVideoSection songId={song?.id} level="level_2" />
        </div>

        {/* Level 2 Analysis */}
        {level2 && (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
              <Icon name="Star" size={24} className="mr-2" />
              Level 2 Analysis
            </h3>
            <div className="text-blue-50 leading-relaxed whitespace-pre-wrap">{level2}</div>
          </div>
        )}
      </div>
    );
  };

  // Level 3 Tab Content
  const renderLevel3Tab = () => {
    const level3 = characterAnalysis?.level3 || [];
    const sectionsArray = Array.isArray(level3) ? level3 : [];
    
    return (
      <div className="space-y-6">
        {/* Mux Video Section for Level 3 */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <MuxVideoSection songId={song?.id} level="level_3" />
        </div>

        {/* Level 3 Sections */}
        {sectionsArray?.length > 0 ? (
          <div className="space-y-4">
            {sectionsArray?.map((section, index) => (
              <div
                key={index}
                className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6"
              >
                {/* Section Name (e.g., "Opening Section") */}
                {section?.section && (
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <Icon 
                      name={index === 0 ? "Play" : index === 1 ? "RotateCcw" : "Square"} 
                      size={20} 
                      className="mr-2 text-blue-300" 
                    />
                    {section?.section}
                  </h4>
                )}
                
                {/* Header - Custom title for this specific section */}
                {section?.header && (
                  <div className="mb-4">
                    <h5 className="text-md font-semibold text-blue-100 mb-2">Header:</h5>
                    <p className="text-blue-50 leading-relaxed">{section?.header}</p>
                  </div>
                )}
                
                {/* Content - Main analysis text */}
                {section?.content && (
                  <div>
                    <h5 className="text-md font-semibold text-blue-100 mb-2">Content:</h5>
                    <p className="text-blue-50 leading-relaxed whitespace-pre-wrap">{section?.content}</p>
                  </div>
                )}
                
                {/* Show message if no content */}
                {!section?.header && !section?.content && (
                  <p className="text-blue-200 italic">No content available for this section</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <p className="text-blue-200 text-center">No Level 3 analysis available</p>
          </div>
        )}
      </div>
    );
  };

  // Level 4 Tab Content
  const renderLevel4Tab = () => {
    const level4 = characterAnalysis?.level4 || {};
    
    return (
      <div className="space-y-6">
        {/* Mux Video Section for Level 4 */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <MuxVideoSection songId={song?.id} level="level_4" />
        </div>

        {/* Level 4 Analysis */}
        {Object.keys(level4)?.length > 0 ? (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
              <Icon name="TrendingUp" size={24} className="mr-2" />
              Level 4 Analysis
            </h3>
            <div className="space-y-6">
              {Object.entries(level4)?.map(([key, value], index) => (
                <div 
                  key={key} 
                  className={`${index > 0 ? 'pt-6 border-t border-white border-opacity-10' : ''}`}
                >
                  <h4 className="font-semibold text-lg text-blue-100 capitalize mb-3 flex items-center">
                    <Icon name="Circle" size={12} className="mr-2 text-blue-400" />
                    {key?.replace(/([A-Z])/g, ' $1')?.trim()}
                  </h4>
                  <div className="ml-6">
                    {renderFormattedContent(value)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <p className="text-blue-200 text-center">No Level 4 analysis available</p>
          </div>
        )}
      </div>
    );
  };

  // Level 5 Tab Content
  const renderLevel5Tab = () => {
    const level5 = characterAnalysis?.level5 || {};
    
    // Check if level5 has phrases array
    const phrasesArray = level5?.phrases || [];
    const hasPhrases = Array.isArray(phrasesArray) && phrasesArray?.length > 0;
    
    return (
      <div className="space-y-6">
        {/* Mux Video Section for Level 5 */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <MuxVideoSection songId={song?.id} level="level_5" />
        </div>

        {/* Level 5 Phrases Analysis */}
        {hasPhrases ? (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
              <Icon name="Zap" size={24} className="mr-2" />
              Level 5 Analysis - Mastery Phrases
            </h3>
            <div className="space-y-6">
              {phrasesArray?.map((phrase, index) => renderLevel5Phrase(phrase, index))}
            </div>
          </div>
        ) : (
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
              <Icon name="Zap" size={24} className="mr-2" />
              Level 5 Analysis
            </h3>
            <p className="text-blue-200 text-center">No Level 5 analysis available</p>
          </div>
        )}
      </div>
    );
  };

  // Helper function to render Level 5 phrases with specific field order
  const renderLevel5Phrase = (phrase, index) => {
    // Support both old schema (phrase/emotion/acting/vocal) and new schema (header/description)
    const phraseHeader = phrase?.header || phrase?.phrase || '';

    // If old schema has individual fields, use them directly
    const hasOldSchema = phrase?.emotion || phrase?.acting || phrase?.vocal;

    // Parse description text to extract Emotion / Acting / Vocal sections
    const parseDescription = (description) => {
      if (!description) return { emotion: '', acting: '', vocal: '', remainder: '' };

      const result = { emotion: '', acting: '', vocal: '', remainder: '' };

      // Try to extract labeled sections (case-insensitive, supports colon or newline after label)
      const emotionMatch = description?.match(/emotion\s*[:\-]\s*([\s\S]*?)(?=\n\s*(?:acting|vocal)\s*[:\-]|$)/i);
      const actingMatch = description?.match(/acting\s*[:\-]\s*([\s\S]*?)(?=\n\s*(?:emotion|vocal)\s*[:\-]|$)/i);
      const vocalMatch = description?.match(/vocal\s*[:\-]\s*([\s\S]*?)(?=\n\s*(?:emotion|acting)\s*[:\-]|$)/i);

      if (emotionMatch) result.emotion = emotionMatch?.[1]?.trim();
      if (actingMatch) result.acting = actingMatch?.[1]?.trim();
      if (vocalMatch) result.vocal = vocalMatch?.[1]?.trim();

      // If no labeled sections found, treat entire description as remainder
      if (!result?.emotion && !result?.acting && !result?.vocal) {
        result.remainder = description?.trim();
      }

      return result;
    };

    const parsed = hasOldSchema
      ? { emotion: phrase?.emotion || '', acting: phrase?.acting || '', vocal: phrase?.vocal || '', remainder: '' }
      : parseDescription(phrase?.description);

    return (
      <div key={phrase?.id || index} className="border border-white border-opacity-10 rounded-lg p-6 bg-white bg-opacity-5">
        {/* Phrase Header - Display First */}
        <div className="mb-4">
          <h5 className="text-md font-semibold text-blue-100 mb-2 flex items-center">
            <Icon name="MessageCircle" size={16} className="mr-2 text-blue-400" />
            Phrase
          </h5>
          {phraseHeader ? (
            <p className="text-blue-50 text-lg leading-relaxed whitespace-pre-wrap italic">
              "{phraseHeader}"
            </p>
          ) : (
            <span className="text-blue-300 italic text-sm">No phrase text</span>
          )}
        </div>
        {/* If description has no labeled sections, show it as full description */}
        {parsed?.remainder ? (
          <div className="mb-4">
            <h6 className="text-sm font-semibold text-blue-100 mb-2 flex items-center">
              <Icon name="FileText" size={14} className="mr-2 text-blue-400" />
              Description
            </h6>
            <p className="text-blue-50 leading-relaxed whitespace-pre-wrap ml-6">
              {parsed?.remainder}
            </p>
          </div>
        ) : (
          <>
            {/* Emotion - Display Second */}
            <div className="mb-4">
              <h6 className="text-sm font-semibold text-blue-100 mb-2 flex items-center">
                <Icon name="Heart" size={14} className="mr-2 text-pink-400" />
                Emotion
              </h6>
              {parsed?.emotion ? (
                <p className="text-blue-50 leading-relaxed whitespace-pre-wrap ml-6">
                  {parsed?.emotion}
                </p>
              ) : (
                <span className="text-blue-300 italic text-sm ml-6">Not specified</span>
              )}
            </div>

            {/* Acting - Display Third */}
            <div className="mb-4">
              <h6 className="text-sm font-semibold text-blue-100 mb-2 flex items-center">
                <Icon name="Users" size={14} className="mr-2 text-green-400" />
                Acting
              </h6>
              {parsed?.acting ? (
                <p className="text-blue-50 leading-relaxed whitespace-pre-wrap ml-6">
                  {parsed?.acting}
                </p>
              ) : (
                <span className="text-blue-300 italic text-sm ml-6">Not specified</span>
              )}
            </div>

            {/* Vocal - Display Fourth */}
            <div>
              <h6 className="text-sm font-semibold text-blue-100 mb-2 flex items-center">
                <Icon name="Music" size={14} className="mr-2 text-purple-400" />
                Vocal
              </h6>
              {parsed?.vocal ? (
                <p className="text-blue-50 leading-relaxed whitespace-pre-wrap ml-6">
                  {parsed?.vocal}
                </p>
              ) : (
                <span className="text-blue-300 italic text-sm ml-6">Not specified</span>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // Helper function to render formatted content
  const renderFormattedContent = (value) => {
    // Handle null/undefined
    if (value === null || value === undefined) {
      return <span className="text-blue-300 italic">Not specified</span>;
    }

    // Handle arrays
    if (Array.isArray(value)) {
      if (value?.length === 0) {
        return <span className="text-blue-300 italic">Empty list</span>;
      }
      return (
        <ul className="list-disc list-inside space-y-2 ml-2">
          {value?.map((item, idx) => (
            <li key={idx} className="text-blue-50">
              {typeof item === 'object' ? (
                <div className="ml-4 mt-1">{renderFormattedContent(item)}</div>
              ) : (
                <span>{String(item)}</span>
              )}
            </li>
          ))}
        </ul>
      );
    }

    // Handle objects
    if (typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries?.length === 0) {
        return <span className="text-blue-300 italic">No data</span>;
      }
      return (
        <div className="space-y-3 ml-4 pl-4 border-l-2 border-blue-400 border-opacity-30">
          {entries?.map(([nestedKey, nestedValue]) => (
            <div key={nestedKey}>
              <p className="font-medium text-blue-200 capitalize mb-1">
                {nestedKey?.replace(/([A-Z])/g, ' $1')?.trim()}:
              </p>
              <div className="ml-3">{renderFormattedContent(nestedValue)}</div>
            </div>
          ))}
        </div>
      );
    }

    // Handle strings and primitives
    return <p className="text-blue-50 whitespace-pre-wrap leading-relaxed">{String(value)}</p>;
  };

  // Character selector UI
  const renderCharacterSelector = () => {
    if (characterAnalyses?.length <= 1) return null;

    return (
      <div className="mb-6 bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-4">
        <label className="block text-sm font-medium text-blue-100 mb-3">
          Select Character Analysis
        </label>
        <div className="flex flex-wrap gap-2">
          {characterAnalyses?.map((analysis, index) => (
            <Link
              key={analysis?.id || index}
              to={analysis?.role?.id ? `/role-detail-page/${analysis?.role?.id}` : '#'}
              onClick={(e) => {
                if (!analysis?.role?.id) {
                  e?.preventDefault();
                }
                handleCharacterSelect(index);
              }}
              className={`px-4 py-2 rounded-lg transition-all ${
                selectedCharacterIndex === index
                  ? 'bg-blue-500 text-white font-semibold' :'bg-white bg-opacity-20 text-blue-100 hover:bg-opacity-30'
              } ${analysis?.role?.id ? 'cursor-pointer hover:underline' : 'cursor-default'}`}
            >
              {analysis?.role?.character_name || `Character ${index + 1}`}
            </Link>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center bg-red-500 bg-opacity-20 border border-red-400 border-opacity-30 rounded-xl p-6 max-w-md">
          <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Error Loading Analysis</h3>
          <p className="text-red-200">{error}</p>
        </div>
      </div>
    );
  }

  if (!characterAnalysis) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center bg-blue-500 bg-opacity-20 border border-blue-400 border-opacity-30 rounded-xl p-8 max-w-lg">
          <Icon name="Users" size={56} className="text-blue-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-3">No Character Role Assigned</h3>
          <p className="text-blue-100 mb-6 leading-relaxed">
            To view detailed analysis and content for this song, please assign a character role to it first. 
            Character roles help organize song content by specific characters and enable level-by-level training materials.
          </p>
          <Link 
            to="/all-roles" 
            className="inline-flex items-center px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors shadow-lg"
          >
            <Icon name="UserPlus" size={20} className="mr-2" />
            Go to Roles Page
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Home' },
    { id: 'level1', label: 'Level 1', icon: 'Award' },
    { id: 'level2', label: 'Level 2', icon: 'Star' },
    { id: 'level3', label: 'Level 3', icon: 'Layers' },
    { id: 'level4', label: 'Level 4', icon: 'TrendingUp' },
    { id: 'level5', label: 'Level 5', icon: 'Zap' }
  ];

  return (
    <div className="space-y-6">
      {/* Character selector */}
      {renderCharacterSelector()}

      {/* Tab Navigation */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-2">
        <div className="flex flex-wrap gap-2">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === tab?.id
                  ? 'bg-blue-500 text-white font-semibold shadow-lg'
                  : 'bg-white bg-opacity-10 text-blue-100 hover:bg-opacity-20'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'level1' && renderLevel1Tab()}
        {activeTab === 'level2' && renderLevel2Tab()}
        {activeTab === 'level3' && renderLevel3Tab()}
        {activeTab === 'level4' && renderLevel4Tab()}
        {activeTab === 'level5' && renderLevel5Tab()}
      </div>

      {/* Upload Modal */}
      <SongFileUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        songId={song?.id}
        onUploadComplete={handleUploadComplete}
      />

      {/* Karaoke Modal */}
      <KaraokeModal
        isOpen={showKaraokeModal}
        onClose={handleCloseKaraoke}
        midiUrl={songFiles?.midiUrl}
        songTitle={song?.title}
      />
    </div>
  );
}